import { usePageViewAnalytics } from "@leagueplatform/analytics";
import { Box, HeadingText, StackLayout } from "@leagueplatform/genesis-core";
import { useIntl } from "@leagueplatform/locales";
import { Masonry } from "@leagueplatform/masonry";
import { useDocumentTitle } from "@leagueplatform/web-common";
import { MASONRY } from "common/constants";
import { useIsBelowTablet } from "hooks/use-is-below-tablet.hook";

// Desktop widths from the Figma design (frame 132131556).
// - PAGE_CONTENT_MAX_WIDTH: outer content area (hero band heading + page body alignment).
// - MASONRY_CONTENT_MAX_WIDTH: inner card-stack wrapper around the BE Masonry node.
const PAGE_CONTENT_MAX_WIDTH = 952;
const MASONRY_CONTENT_MAX_WIDTH = 644;

/**
 * Customer Support page (QUEST-1860 / SET-09).
 *
 * The bulk of the screen — phone card, chatbot card, illustration,
 * copy, layout — is delivered by Backend as a Masonry node
 * `customerSupport$root` (see QUEST-478 / QUEST-2012). The Web side
 * owns the route, document title, page-view analytics, the green
 * "Customer Support" hero band (matching peer Settings sub-pages
 * like Personal Information / About Quest), and the Masonry container.
 *
 * The phone card's click-to-call (`tel:`) behavior is driven entirely
 * by the BE `callServiceCenterCard` node's `onClick` deeplink action.
 * See `utils/init-masonry-registry.ts` for the Quest-side deeplink
 * handler that opens `tel:` / `mailto:` / `sms:` schemes via the
 * native app picker.
 *
 * Android mirrors the Masonry pattern (see QUEST-488 / QUEST-2015).
 */
export const ContactUs = () => {
  const { formatMessage } = useIntl();
  const isBelowTablet = useIsBelowTablet();

  useDocumentTitle(formatMessage({ id: "STR_CUSTOMER_SUPPORT" }));
  usePageViewAnalytics({
    product_area: "settings",
    screen_name: "Customer Support",
  });

  return (
    <>
      {/* Green hero band — full-width, heading aligned to content max-width. */}
      <Box
        css={{
          backgroundColor: "$surfaceBackgroundTertiary",
          height: isBelowTablet ? "auto" : 140,
          paddingInline: "$two",
          paddingBlock: isBelowTablet ? "$two" : "$none",
        }}
      >
        <StackLayout
          orientation="horizontal"
          verticalAlignment="center"
          css={{
            maxWidth: PAGE_CONTENT_MAX_WIDTH,
            marginInline: "auto",
            height: "100%",
          }}
        >
          <HeadingText level="1" size={isBelowTablet ? "xl" : "xxl"}>
            {formatMessage({ id: "STR_CUSTOMER_SUPPORT" })}
          </HeadingText>
        </StackLayout>
      </Box>

      {/* Page content area (952px on desktop) wrapping the inner Masonry card stack (644px). */}
      <Box
        css={{
          marginInline: "auto",
          paddingInline: "$two",
          paddingBlock: "$two",
          maxWidth: PAGE_CONTENT_MAX_WIDTH,
          "@mobile": {
            paddingInline: "$one",
          },
        }}
      >
        <StackLayout
          orientation="vertical"
          verticalAlignment="center"
          horizontalAlignment="stretch"
          css={{
            marginInline: "auto",
            maxWidth: MASONRY_CONTENT_MAX_WIDTH,
            width: "100%",
          }}
        >
          <Masonry rootNodeId={MASONRY.ROOT_NODE_ID_CONTACT_US} />
        </StackLayout>
      </Box>
    </>
  );
};
